import React, { useState, useEffect } from 'react';
import { Phone, Calendar, User, MessageSquare, Plus, Filter } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const CallInfoPanel = ({ leads, onCallLogged }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callFormData, setCallFormData] = useState({
    lead_id: '',
    call_duration: 0,
    call_outcome: 'successful',
    call_notes: '',
    next_call_scheduled: '',
    made_by: '',
    direction: 'outbound',
    phone_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOutcome, setFilterOutcome] = useState('all');

  useEffect(() => {
    if (leads?.length > 0) {
      setSelectedLead(leads?.[0]);
    }
  }, [leads]);

  useEffect(() => {
    if (selectedLead?.id) {
      loadCallLogs();
    }
  }, [selectedLead]);

  const loadCallLogs = async () => {
    if (!selectedLead?.id) return;
    
    setLoading(true);
    try {
      const { data, error: callError } = await funnelService?.getCallLogs(selectedLead?.id);
      if (callError) {
        setError(callError);
      } else {
        setCallLogs(data || []);
      }
    } catch (err) {
      setError('Failed to load call logs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCall = () => {
    setCallFormData({
      lead_id: selectedLead?.id || '',
      call_duration: 0,
      call_outcome: 'successful',
      call_notes: '',
      next_call_scheduled: '',
      made_by: selectedLead?.user_id || '',
      direction: 'outbound',
      phone_number: selectedLead?.communication_contacts?.phone || ''
    });
    setShowCallModal(true);
  };

  const handleCallSubmit = async (e) => {
    e?.preventDefault();
    
    if (!callFormData?.lead_id) {
      setError('Please select a lead');
      return;
    }

    try {
      const { data, error: submitError } = await funnelService?.logCall({
        ...callFormData,
        call_duration: parseInt(callFormData?.call_duration) || 0,
        next_call_scheduled: callFormData?.next_call_scheduled || null
      });

      if (submitError) {
        setError(submitError);
      } else {
        setShowCallModal(false);
        setCallFormData({
          lead_id: '',
          call_duration: 0,
          call_outcome: 'successful',
          call_notes: '',
          next_call_scheduled: '',
          made_by: '',
          direction: 'outbound',
          phone_number: ''
        });
        await loadCallLogs();
        onCallLogged?.();
      }
    } catch (err) {
      setError('Failed to log call');
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      successful: 'bg-green-100 text-green-800 border-green-200',
      no_answer: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      busy: 'bg-orange-100 text-orange-800 border-orange-200',
      voicemail: 'bg-blue-100 text-blue-800 border-blue-200',
      callback_requested: 'bg-purple-100 text-purple-800 border-purple-200',
      not_interested: 'bg-red-100 text-red-800 border-red-200',
      interested: 'bg-green-100 text-green-800 border-green-200',
      follow_up_scheduled: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      sale_closed: 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return colors?.[outcome] || colors?.successful;
  };

  const outcomeOptions = [
    { value: 'successful', label: 'Successful' },
    { value: 'no_answer', label: 'No Answer' },
    { value: 'busy', label: 'Busy' },
    { value: 'voicemail', label: 'Voicemail' },
    { value: 'callback_requested', label: 'Callback Requested' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'interested', label: 'Interested' },
    { value: 'follow_up_scheduled', label: 'Follow-up Scheduled' },
    { value: 'sale_closed', label: 'Sale Closed' }
  ];

  const filteredLogs = filterOutcome === 'all' 
    ? callLogs 
    : callLogs?.filter(log => log?.outcome === filterOutcome);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Call Information & Logs</h3>
          <p className="text-sm text-gray-600">Track call activities and outcomes for your leads</p>
        </div>
        <button
          onClick={handleCreateCall}
          disabled={!selectedLead}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
        >
          <Plus className="h-4 w-4 mr-2" />
          Log Call
        </button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            ×
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Select Lead</h4>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {leads?.map((lead) => (
                <button
                  key={lead?.id}
                  onClick={() => setSelectedLead(lead)}
                  className={`
                    w-full text-left p-3 rounded-md border transition-colors
                    ${selectedLead?.id === lead?.id
                      ? 'bg-blue-50 border-blue-200 text-blue-900' :'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {lead?.communication_contacts?.name || 'Unknown Contact'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {lead?.communication_contacts?.company || 'No Company'}
                      </p>
                      <div className="flex items-center mt-1">
                        <Phone className="h-3 w-3 mr-1 text-gray-400" />
                        <p className="text-xs text-gray-500">
                          {lead?.communication_contacts?.phone || 'No phone'}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              
              {leads?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No leads available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call Logs */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  Call History
                  {selectedLead && (
                    <span className="ml-2 text-sm text-gray-500">
                      for {selectedLead?.communication_contacts?.name}
                    </span>
                  )}
                </h4>
                
                {/* Filter */}
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={filterOutcome}
                    onChange={(e) => setFilterOutcome(e?.target?.value)}
                    className="text-sm border-gray-300 rounded-md"
                  >
                    <option value="all">All Outcomes</option>
                    {outcomeOptions?.map((option) => (
                      <option key={option?.value} value={option?.value}>
                        {option?.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="p-4">
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              )}

              <div className="space-y-4">
                {filteredLogs?.map((log) => (
                  <div key={log?.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(log?.call_started_at || log?.created_at)?.toLocaleString()}
                          {log?.user_profiles && (
                            <>
                              <span className="mx-2">•</span>
                              <User className="h-3 w-3 mr-1" />
                              {log?.user_profiles?.first_name} {log?.user_profiles?.last_name}
                            </>
                          )}
                        </div>

                        {log?.call_notes && (
                          <div className="mb-2">
                            <div className="flex items-start">
                              <MessageSquare className="h-3 w-3 mr-2 mt-1 text-gray-400" />
                              <p className="text-sm text-gray-700">{log?.call_notes}</p>
                            </div>
                          </div>
                        )}

                        {log?.follow_up_scheduled && (
                          <div className="flex items-center text-sm text-orange-600">
                            <Calendar className="h-3 w-3 mr-1" />
                            Follow-up scheduled
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {filteredLogs?.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    <Phone className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      {selectedLead 
                        ? (filterOutcome === 'all' ? 'No call logs found' : `No ${filterOutcome} calls found`)
                        : 'Select a lead to view call history'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Call Modal */}
      {showCallModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-md shadow-lg">
            <form onSubmit={handleCallSubmit}>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Log Call</h3>
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lead
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                      {selectedLead?.communication_contacts?.name || 'No lead selected'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={callFormData?.phone_number}
                      onChange={(e) => setCallFormData(prev => ({ ...prev, phone_number: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Direction
                    </label>
                    <select
                      value={callFormData?.direction}
                      onChange={(e) => setCallFormData(prev => ({ ...prev, direction: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    >
                      <option value="outbound">Outbound</option>
                      <option value="inbound">Inbound</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Outcome
                    </label>
                    <select
                      value={callFormData?.call_outcome}
                      onChange={(e) => setCallFormData(prev => ({ ...prev, call_outcome: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    >
                      {outcomeOptions?.map((option) => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Call Notes
                    </label>
                    <textarea
                      rows={3}
                      value={callFormData?.call_notes}
                      onChange={(e) => setCallFormData(prev => ({ ...prev, call_notes: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter notes about the call..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Next Call Scheduled
                    </label>
                    <input
                      type="datetime-local"
                      value={callFormData?.next_call_scheduled}
                      onChange={(e) => setCallFormData(prev => ({ ...prev, next_call_scheduled: e?.target?.value }))}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCallModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Log Call
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallInfoPanel;